// ЭКРАН ШАГА 6 — overlay в статике. B8-3 `3e619875cbb881899434ffc29c5bcc32`.
//
// ИСТОЧНИК — борд `Life Map - s6 Overlay v10.html` (sha256 `28c1a6c3…`), `frame()` `:54–74`, шаблоны
// карточек `:34–51`; краевые — `Edge Review v1` (`19701b02…`); спека шага 6
// `3b419875cbb88198b9c8cb66fbd53662`; DoR s960 тикета. Где борд отстал от D&R — прав код (DoR п. 1):
//   · бровь при scatter — `overlay · none leads` (`s824.d5`), не `the field`;
//   · карточка `the carriers` при scatter — только `no single line carries it.` (`s531.d3`);
//   · строки `evidence · stages` при scatter нет (`s849.d1`);
//   · `levelBand` — из движка (`viewIV`), не средним борда (`s531.d5`);
//   · ярлык поля цитаты — джойн `perStage` по этапу ВЫБРАННОЙ цитаты (`viewVI`), T-код не печатается (`s531.d4`);
//   · имя main pattern — `patternName()` + `MECH` §V, готового имени в движке нет.
//
// ⭐ ДАННЫЕ — ВЬЮ ПАСПОРТА, А НЕ СВОЙ РАСЧЁТ (как шаг 5). `viewIV` — расклад, члены (у раскола — в порядке
// брови шага 5), класс геометрии, уровень, `carrierStages.indices` (сырой `detect()` их не несёт, cc-93);
// `viewV` — вход `patternName`; `viewVI` — стек цитат с ярлыком поля. Одна находка движка на все поверхности:
// что названо на шаге 5, то и здесь.
//
// ⭐ ЦИТАТА (`s960.d1`, сводит `s824.d3` с `s918.d1`): с ПЕРВОГО по порядку этапа строки evidence, у которого
// в стеке есть цитата; ни у одного нет — карточки нет вовсе (вид кадров D и G). При scatter строки evidence
// нет, и отбор — `s824.d3` целиком: первый несущий этап, нет у него — первая цитата стека. Фолбэка на стек
// вне scatter нет: цитата с ненесущего этапа на экран не идёт.
//
// ⭐ CLOSE-БЛОК (B8-5) — конец экрана вместо ряда футера (`footerNav.js`: шаг 6 без ряда). Одна `.cta` ведёт в
// гейт шага 7 обычным `next()` — допуск адреса тот же, что у CTA оболочки. С B10-4 перед `next()` — цепочка
// сабмита `runSubmit` (`submitChain.js`): не прошла — переход на адрес проблемы и строка слота вместо гейта.
// Reveal тихий (`s453.d2`): анимаций нет, состояний отсутствия System 2 нет — пропадает только карточка цитаты.
//
// ⭐ ЦИТАТА ПРИХОДИТ ИЗ КОНВЕРТА (строка 73, `s987.d3`): судью зовёт оболочка на выходе с шага 3, паспорт шага
// (`useDocumentPassport`) берёт `system2` из `quotes`. Ответ пришёл, пока человек на экране, — карточка
// появляется без анонса. CTA сначала проверяет ввод (отказ — сразу, без ожидания судьи), потом ЖДЁТ судью
// (`awaitQuotes`, в пределах транспорта) и только тогда ведёт в гейт: шаг 7, Open, PDF и слепок строятся с
// тем же `system2`. Повторное нажатие во время ожидания ничего не делает; кнопка несёт `aria-busy`.

import { useCallback, useMemo, useRef, useState } from 'react';

import { useWizard } from '../WizardProvider.jsx';
import { useDocumentPassport } from '../documentPassport.js';
import { runSubmit } from '../submitChain.js';
import { SUBMIT_COPY } from '../submitCopy.js';
import { viewIV, viewV, viewVI } from '../../render/viewFromPassport.js';
import { ROMAN } from '../../roman.js';
import { formatAddress } from '../address.js';
import { MECH, italicPhraseOverlay, patternName } from '../../copy/chartCopy.js';
import { chartInput, chartSvg } from './s6Chart.js';
import { S4_LAYERS } from './s4Chart.js';
import { OVERLAY_FIELD, browLabel, carriersCard, showEvidenceLine } from './s6Copy.js';
import {
  S6_CARRIERS_HEAD, S6_CHART_RIGHT, S6_EV_HEAD, S6_EV_JOIN, S6_EV_STAGES, S6_EYEBROW, S6_GTAG_HEAD,
  S6_CLOSE, S6_GTAG_SEP, S6_MP_EYEBROW, S6_MP_RAIL, S6_QUOTE_AT, S6_QUOTE_FIELD, S6_QUOTE_HEAD, S6_SUB, S6_TITLE,
} from './s6Frame.js';

/**
 * Печатное имя слоя шага 6 — имя деки (`sense of security`), как на шагах 3–5. ⚠️ Борд печатает ключ
 * строчными (`security`, `yourself`) — это дизайн-тайм, не имя продукта; вторым именем той же находки
 * рядом с шагом 5 оно было бы вторым голосом (handoff §1: «другого состава или другого имени не может быть»).
 */
const LAYER = Object.fromEntries(S4_LAYERS.map((L) => [L.key, L]));
const lname = (k) => (LAYER[k] ? LAYER[k].name : String(k).toLowerCase());

/**
 * Цитата карточки по `s960.d1` / `s824.d3` / `s963.d3`. Стек — `viewVI(passport).stack` плюс `qn` (цитата
 * текущего этапа = этап N−1, `s963.d3`): реальная цитата — `reason === null` (тихие и пустые несут причину),
 * ярлык поля уже приджойнен по её этапу. При scatter — первая реальная цитата стека, тихие пропускаются.
 *
 * @param {Array<{stage:number,text:string,field:string|null,reason:string|null}>} stack
 * @param {number[]} ev несущие этапы (`carrierStages.indices`)
 * @param {boolean} scatter
 */
export function pickQuote(stack, ev, scatter) {
  const real = stack.filter((c) => c.reason === null && c.text);
  const at = (i) => real.find((c) => c.stage === i) || null;
  const order = [...ev].sort((a, b) => a - b);
  if (!scatter) {
    for (const i of order) {
      const q = at(i);
      if (q) return q;
    }
    return null;
  }
  return (order.length ? at(order[0]) : null) || real[0] || null;
}

/**
 * Всё, что печатает экран шага 6, из паспорта. Чистая — ради синтетики по раскладам и корпуса паспортов.
 *
 * @param {object} passport выход `buildPassport`
 */
export function stepSixView(passport) {
  const v = viewIV(passport);
  const config = v.carrierConfig;
  const scatter = config === 'scatter';
  const mp = passport.system1.mainPattern || {};
  // Члены брови, механизма и пилюль. У раскола «двое» — пара шага 5 (`viewIV`: несущая + противник);
  // «блок против блока» (`group-vs-group`) — все слои обоих блоков: ветка `> 2` (`s464.d2`) читает их число.
  const members = config === 'split' && mp.type === 'group-vs-group' && Array.isArray(mp.layers)
    ? mp.layers.slice()
    : v.members.slice();
  const n = v.carrier.length;
  const ev = v.carrierIdx.filter((i) => Number.isInteger(i) && i >= 0 && i < n);
  const card = carriersCard(config, members);
  const vi = viewVI(passport);
  const q = pickQuote(vi.qn ? [...vi.stack, vi.qn] : vi.stack, ev, scatter);
  return {
    config,
    brow: browLabel(config, members, lname),
    // Форма overlay (U+2019), ключ plateau × `levelBand` движка; фолбэк — общий с §IV и шагом 5.
    italic: italicPhraseOverlay(v),
    carriers: { ...card, pills: card.pills.map(lname) },
    pattern: scatter ? MECH.scatter : patternName(viewV(passport), lname).t,
    evidence: showEvidenceLine(config) && ev.length ? ev.map((i) => ROMAN[i]).join(S6_EV_JOIN) : null,
    ev,
    // Чарт: даты этапов + силуэт + несущая. Без временной оси (этап без даты) — чарта нет.
    chart: (() => { const inp = chartInput(passport); return inp ? chartSvg(inp) : null; })(),
    quote: q && {
      stage: q.stage,
      text: q.text,
      field: OVERLAY_FIELD[q.field] ?? null,
      roman: ROMAN[q.stage],
    },
  };
}


/**
 * Сколько держится строка отказа сабмита (B10-4). Канал `announce` живёт по таймеру — другого способа
 * пережить смену экрана у визарда нет. 6 с — время прочесть строку слота (до 80 знаков) после
 * перехода; у прощального тоста шага 2 1600 мс, но там три слова и никакого действия.
 */
export const SUBMIT_TOAST_MS = 6000;

export default function StepSix() {
  const { session, next, goTo, announce, awaitQuotes } = useWizard();
  const passport = useDocumentPassport(session);
  const view = useMemo(() => stepSixView(passport), [passport]);
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  // ⭐ B10-4, вариант Б `s929.d1`: цепочка сабмита на нажатии, не на маунте шага 7. Отказ — переход на адрес
  // проблемы (B10-2) и строка слота (B10-3) через `announce`; адреса нет — шаг 1, где стоит `start over`.
  const onCta = useCallback(async (e) => {
    e.preventDefault();
    if (pending.current) return;
    const r = runSubmit(session);
    if (!r.ok) {
      goTo(r.address ?? { step: 1 });
      announce(<span data-testid="submit-error" data-slot={r.slot}>{SUBMIT_COPY[r.slot]}</span>, SUBMIT_TOAST_MS);
      return;
    }
    pending.current = true;
    setBusy(true);
    try {
      if (awaitQuotes) await awaitQuotes();
    } finally {
      pending.current = false;
      setBusy(false);
    }
    next();
  }, [session, next, goTo, announce, awaitQuotes]);
  return <StepSixScreen view={view} onCta={onCta} busy={busy} />;
}

/** Адрес гейта для `href` CTA — переход делает `onCta`, ссылка остаётся ссылкой без скрипта. */
const GATE_HREF = formatAddress({ step: 7 });

/** Разметка экрана по вью-модели. Экспортирована ради синтетики по раскладам. */
export function StepSixScreen({ view, onCta, busy = false }) {
  const { carriers, quote } = view;
  return (
    <div data-testid="s6-screen" className="s6-screen" data-config={view.config}>
      <div data-testid="s6-eyebrow" className="s6-eyb">{S6_EYEBROW}</div>
      <h2 data-testid="s6-title" className="s6-ttl">{S6_TITLE.pre}<em>{S6_TITLE.em}</em><br />{S6_TITLE.post}</h2>
      <p data-testid="s6-sub" className="s6-sub">{S6_SUB}</p>

      <div className="s6-cw">
        <span className="s6-corner tl" /><span className="s6-corner tr" /><span className="s6-corner bl" /><span className="s6-corner br" />
        <div data-testid="s6-gtag" className="s6-gtag">{S6_GTAG_HEAD}<em>{S6_GTAG_SEP}</em><span data-testid="s6-brow">{view.brow}</span></div>
        <div className="s6-eyb-r">{S6_CHART_RIGHT}</div>
        {/* Чарт силуэта и красной — B8-4, `chart(…,'terrain',356,236,{})` борда байт в байт (`s6Chart.js`).
            Строка SVG собрана из чисел паспорта, текста человека в ней нет. Борд кладёт SVG прямо в `.cw`. */}
        {view.chart && <div data-testid="s6-chart" dangerouslySetInnerHTML={{ __html: view.chart }} />}
      </div>

      <p data-testid="s6-italic" className="s6-iq">{view.italic}</p>

      <div data-testid="s6-cards" className="s6-cards">
        <div data-testid="s6-carriers" className="s6-card">
          <div className="s6-card-hd"><span><b>{S6_CARRIERS_HEAD}</b></span><span className="n">{view.config}</span></div>
          {carriers.scatter
            ? <p data-testid="s6-carriers-line">{carriers.sentence}</p>
            : (
              <>
                <p data-testid="s6-carriers-line">{carriers.sentence.slice(0, -(carriers.mech.length + 1))}<em>{`${carriers.mech}.`}</em></p>
                <div data-testid="s6-pills" className="s6-pills">
                  {carriers.pills.map((p) => <span key={p} className="s6-pill">{p}</span>)}
                </div>
              </>
            )}
        </div>

        <div data-testid="s6-mp" className="s6-card s6-mpc">
          <span className="s6-mp-rail">{S6_MP_RAIL}</span>
          <div className="s6-mp-body">
            <div className="s6-mp-eyb">{S6_MP_EYEBROW}</div>
            <div data-testid="s6-mp-name" className="s6-mp-name">{view.pattern}</div>
            {view.evidence === null ? null : (
              <div data-testid="s6-evidence" className="s6-mp-ev">{S6_EV_HEAD} <em>{`${S6_EV_STAGES} ${view.evidence}`}</em></div>
            )}
          </div>
        </div>

        {quote ? (
          <div data-testid="s6-quote" className="s6-card s6-qc" data-stage={quote.stage}>
            <div className="s6-card-hd">
              <span><b>{S6_QUOTE_HEAD}</b></span>
              {quote.field ? <span data-testid="s6-quote-field" className="n">{`${S6_QUOTE_FIELD}${quote.field}`}</span> : null}
            </div>
            <p data-testid="s6-quote-text" className="s6-qt">{`“${quote.text}”`}</p>
            <div className="s6-qt-at"><span className="s6-qt-rule" />{`${S6_QUOTE_AT}${quote.roman}`}</div>
          </div>
        ) : null}
      </div>

      <div data-testid="s6-close" className="s6-close">
        <div data-testid="s6-cta-eyb" className="s6-cta-eyb">{S6_CLOSE.eyebrow}</div>
        <p data-testid="s6-close-q" className="s6-close-q">{S6_CLOSE.q.pre}<em>{S6_CLOSE.q.em}</em></p>
        <ul data-testid="s6-cta-list" className="s6-cta-list">
          {S6_CLOSE.list.map((l) => <li key={l}>{l}</li>)}
        </ul>
        <a data-testid="s6-cta" className="s6-cta" href={GATE_HREF} onClick={onCta} aria-busy={busy || undefined}>{S6_CLOSE.cta} <span>{S6_CLOSE.arrow}</span></a>
        <div data-testid="s6-cta-sub" className="s6-cta-sub">{S6_CLOSE.sub}</div>
      </div>
    </div>
  );
}
